import React, { createContext, useContext, useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface Progress {
  currentRow: number;
  completedSteps: string[];
  notes: string;
  isFinished?: boolean;
}

interface PatternProgress {
  [patternId: string]: Progress;
}

interface PatternProgressContextType {
  progress: PatternProgress;
  updateProgress: (patternId: string, updates: Partial<Progress>) => Promise<void>;
  getProgress: (patternId: string) => Progress;
}

const defaultProgress: Progress = {
  currentRow: 1,
  completedSteps: [],
  notes: '',
};

const PatternProgressContext = createContext<PatternProgressContextType | null>(null);

export function PatternProgressProvider({ children }: { children: React.ReactNode }) {
  const [progress, setProgress] = useState<PatternProgress>({});

  const updateProgress = async (patternId: string, updates: Partial<Progress>) => {
    try {
      setProgress(prev => ({
        ...prev,
        [patternId]: {
          ...defaultProgress,
          ...(prev[patternId] || {}),
          ...updates,
        },
      }));

      // Save to local storage as backup
      localStorage.setItem(`pattern-progress-${patternId}`, JSON.stringify({
        ...defaultProgress,
        ...(progress[patternId] || {}),
        ...updates,
      }));
      
    } catch (error) {
      console.error('Error updating progress:', error);
      toast.error('Failed to save progress');
    }
  };

  const getProgress = (patternId: string): Progress => {
    // Try to get from state first
    if (progress[patternId]) {
      return progress[patternId];
    }

    // Try to get from local storage
    const stored = localStorage.getItem(`pattern-progress-${patternId}`);
    if (stored) {
      const parsedStored = JSON.parse(stored);
      setProgress(prev => ({
        ...prev,
        [patternId]: parsedStored,
      }));
      return parsedStored;
    }

    // Return default if nothing found
    return defaultProgress;
  };

  return (
    <PatternProgressContext.Provider value={{ progress, updateProgress, getProgress }}>
      {children}
    </PatternProgressContext.Provider>
  );
}

export const usePatternProgress = () => {
  const context = useContext(PatternProgressContext);
  if (!context) {
    throw new Error('usePatternProgress must be used within a PatternProgressProvider');
  }
  return context;
};
